import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RichTextEditor } from '@/components/rich-text';
import { Upload } from 'lucide-react';
import { useCreateMultiCourseLeaveRequestMutation } from '@/state/course';
import { toast } from 'sonner';

interface LeaveRequestDialogProps {
  tenant: string;
  courseId: number;
  studentId: string;
}

const getTodayDateISO = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate()
  ).padStart(2, '0')}`;
};

const hasMeaningfulReason = (value: string) => {
  const trimmed = (value || '').trim();
  if (!trimmed) return false;

  if (trimmed.startsWith('{"root"')) {
    try {
      const parsed = JSON.parse(trimmed) as {
        root?: { children?: Array<{ text?: string; children?: unknown[] }> };
      };
      const queue = [...(parsed.root?.children || [])] as Array<{
        text?: string;
        children?: unknown[];
      }>;

      while (queue.length > 0) {
        const node = queue.shift();
        if (!node) continue;
        if ((node.text || '').trim()) return true;
        if (Array.isArray(node.children)) {
          queue.push(
            ...node.children.filter(Boolean).map((child) => child as {
              text?: string;
              children?: unknown[];
            })
          );
        }
      }
      return false;
    } catch {
      return false;
    }
  }

  // Handle HTML fallback.
  const textOnly = trimmed.replace(/<[^>]*>/g, '').trim();
  return Boolean(textOnly);
};

export const LeaveRequestDialog = ({
  tenant,
  courseId,
  studentId,
}: LeaveRequestDialogProps) => {
  const [open, setOpen] = useState(false);
  const [leaveFrom, setLeaveFrom] = useState('');
  const [leaveTo, setLeaveTo] = useState('');
  const [leaveReason, setLeaveReason] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [errorText, setErrorText] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const serializedGetterRef = useRef<(() => string) | null>(null);
  const [createLeaveRequest, { isLoading }] = useCreateMultiCourseLeaveRequestMutation();

  const resetForm = () => {
    setLeaveFrom('');
    setLeaveTo('');
    setLeaveReason('');
    setAttachment(null);
    setErrorText('');
  };

  const validate = () => {
    const today = getTodayDateISO();
    const serializedReason = serializedGetterRef.current?.() || leaveReason;
    const hasReason = hasMeaningfulReason(serializedReason);

    if (!leaveFrom) {
      setErrorText('Start date is required.');
      return false;
    }
    if (leaveFrom < today) {
      setErrorText('Start date must be today or a future date.');
      return false;
    }
    if (!leaveTo) {
      setErrorText('End date is required.');
      return false;
    }
    if (leaveTo < leaveFrom) {
      setErrorText('End date must be greater than or equal to start date.');
      return false;
    }
    if (!hasReason) {
      setErrorText('Reason for leave request is required.');
      return false;
    }

    setErrorText('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const serializedReason = serializedGetterRef.current?.() || leaveReason;
      const formData = new FormData();
      formData.append('leave_from', leaveFrom);
      formData.append('leave_to', leaveTo);
      formData.append('leave_reason', serializedReason);
      formData.append('student', studentId);
      formData.append('course', String(courseId));
      formData.append('supporting_document', attachment || '');

      await createLeaveRequest({
        tenant,
        data: formData,
      }).unwrap();

      toast.success('Leave request submitted successfully');
      setOpen(false);
      resetForm();
    } catch {
      toast.error('Failed to submit leave request');
    }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-9 shrink-0"
        onClick={() => setOpen(true)}
      >
        Add leave request
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) resetForm();
        }}
      >
        <DialogContent className="flex max-h-[92vh] w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] flex-col gap-3 overflow-hidden p-4 pr-10 sm:max-w-5xl sm:p-6 sm:pr-12 [&>[data-slot=dialog-close]]:top-3 [&>[data-slot=dialog-close]]:right-3">
          <DialogHeader>
            <DialogTitle>Leave request form</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto pr-1">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <label className="text-sm font-medium">Start date</label>
                <Input
                  type="date"
                  value={leaveFrom}
                  min={getTodayDateISO()}
                  onChange={(e) => setLeaveFrom(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">End date</label>
                <Input
                  type="date"
                  value={leaveTo}
                  min={leaveFrom || getTodayDateISO()}
                  onChange={(e) => setLeaveTo(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Reason for leave request</label>
              <RichTextEditor
                value={leaveReason}
                onChange={setLeaveReason}
                placeholder="Reason for leave request"
                minHeight={220}
                onProvideGetSerializedState={(getSerializedState) => {
                  serializedGetterRef.current = getSerializedState;
                }}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Attachment</label>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files?.[0];
                  if (file) setAttachment(file);
                }}
                className="w-full rounded-xl border-2 border-dashed border-muted-foreground/30 bg-background/70 px-4 py-8 text-center text-muted-foreground hover:border-primary/40"
              >
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-5 w-5" />
                  <p className="font-medium">Drag and drop files here</p>
                  <p className="text-sm">Supported file types are PDF | PNG | JPEG</p>
                  {attachment && (
                    <p className="text-sm text-foreground">Selected: {attachment.name}</p>
                  )}
                </div>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setAttachment(file);
                }}
              />
            </div>

            {errorText ? <p className="text-sm text-destructive">{errorText}</p> : null}
          </div>

          <DialogFooter className="pt-1">
            <Button type="button" onClick={handleSubmit} disabled={isLoading}>
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
